import { Button, Modal, Select, Tag } from 'antd';
import { CaretLeftOutlined, CaretRightOutlined, CloseOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import HighlightedContent from './HighlightedContent';
import Fullscreenable from '../ui/Fullscreenable';
import htmlStringToReactNodes from '../../helpers/htmlStringToReactNodes';
import setQueryParamsOnURL from '../../helpers/setQueryParamsOnURL';
import withAppOrigin, { appEnvName } from '../../helpers/withAppOrigin';
import { useSession } from '../../lib/hooks';
const TabNavigation = ({
  step,
  updateStep,
  previews
}) => <div style={{
  display: 'flex',
  gap: 6,
  marginBottom: 6
}} data-sentry-component="TabNavigation" data-sentry-source-file="ActivityPreview.jsx">
    <Button onClick={() => updateStep(step - 1)} icon={<CaretLeftOutlined />} shape="circle" type="primary" disabled={step == 0} data-sentry-element="Button" data-sentry-source-file="ActivityPreview.jsx" />
    <Select className="preview-select" value={step} onChange={value => updateStep(value)} options={previews.map(({
    name
  }, index) => ({
    key: index,
    value: index,
    title: name,
    label: <>
            <b children={`Activity ${index + 1} of ${previews.length}: `} />
            <i children={name} />
          </>
  }))} data-sentry-element="Select" data-sentry-source-file="ActivityPreview.jsx" />
    <Button onClick={() => updateStep(step + 1)} shape="circle" icon={<CaretRightOutlined />} type="primary" disabled={step + 1 >= previews.length} data-sentry-element="Button" data-sentry-source-file="ActivityPreview.jsx" />
  </div>;
const ActivityPreview = ({
  onClose,
  previews,
  action,
  actionButton,
  title,
  tag
}) => {
  const iframeRef = useRef();
  const {
    user
  } = useSession();
  const [step, setStep] = useState(0);
  const [moduleLang, setModuleLang] = useState();
  const [showRetry, setShowRetry] = useState(false);
  const {
    currentURL,
    urlLang
  } = useMemo(() => {
    if (!(user && previews && previews[step].url)) {
      return {};
    }
    const srcURL = withAppOrigin(previews[step].url);
    srcURL.searchParams.set('bookmarks', false);
    srcURL.searchParams.set('reporting', true);
    let lang = srcURL.searchParams.get('language') || 'en';
    const intent_url = srcURL.searchParams.get('intent_url');
    if (intent_url) {
      const intent = new URL(intent_url);
      intent.searchParams.set('userId', user.id);
      intent.searchParams.set('userType', 'Student');
      const appEnv = appEnvName();
      if (appEnv) {
        intent.searchParams.set('_bookbuddies_env', appEnv);
      }
      srcURL.searchParams.set('intent_url', intent.href);
      lang = intent.pathname.split('/').at(-1);
    } else {
      srcURL.searchParams.set('userId', user.id);
      srcURL.searchParams.set('userType', 'Student');
    }
    return {
      currentURL: srcURL.href,
      urlLang: lang
    };
  }, [previews, step, user]);
  const currentLearningObjectiveHTML = previews && previews[step].learningObjectiveHTML;
  if (moduleLang == null && urlLang) {
    setModuleLang(urlLang);
  }
  useEffect(() => {
    if (!currentURL) {
      return;
    }
    const expectedOrigin = new URL(currentURL).origin;
    const handler = ({
      data,
      origin,
      source
    }) => {
      if (expectedOrigin !== origin) {
        return;
      }
      if (iframeRef.current?.contentWindow !== source) {
        return;
      }
      if (['logout', 'exitPreview'].includes(data.event)) {
        setShowRetry(true);
      }
      if (['open'].includes(data.event)) {
        window.open(data.args[0]);
      }
      if (['languageChange'].includes(data.event)) {
        setModuleLang(data.args[0]);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [currentURL]);
  const closeHandler = () => {
    updateStep(0);
    onClose();
  };
  const updateStep = newVal => {
    setShowRetry(newVal === step ? showRetry : false);
    setStep(newVal);
  };
  if (!currentURL) {
    return null;
  }
  return <Modal title={<span style={{
    display: 'flex',
    gap: 6,
    alignItems: 'center'
  }}>
          <Tag color="purple" children={tag} />
          {title}
          <span className="spacer" style={{
      flexGrow: 1
    }} />
          {actionButton ?? (action ? <Button size="small" className="button-tag" type={action.type} onClick={action.onTrigger}>
              {action.label}
            </Button> : null)}
          <Button type="link" aria-label="Close" icon={<CloseOutlined />} onClick={closeHandler} />
        </span>} open centered destroyOnClose closable={false} footer={null} onCancel={closeHandler} width={800 + 100 + 24 * 2} data-sentry-element="Modal" data-sentry-component="ActivityPreview" data-sentry-source-file="ActivityPreview.jsx">
      {previews.length > 1 && <TabNavigation step={step} updateStep={updateStep} previews={previews} />}
      <Fullscreenable data-sentry-element="Fullscreenable" data-sentry-source-file="ActivityPreview.jsx">
        {showRetry ? <div style={{
        width: '100%',
        height: '100%',
        minHeight: 600,
        position: 'relative',
        containerType: 'size'
      }}>
            <img src={`/assets/activity_preview_retry_btn_${moduleLang}.png`} alt="replay activity" style={{
          width: '100%',
          margin: 'auto',
          inset: 0,
          maxWidth: 'min(calc(4/3*100cqh), 100%)',
          display: 'inline-block',
          position: 'absolute',
          border: 0
        }} />
            <button aria-label="replay" onClick={() => setShowRetry(false)} style={{
          display: 'inline-block',
          position: 'absolute',
          border: 0,
          cursor: 'pointer',
          width: '18%',
          maxWidth: 'min(calc(4/3*18cqh), 18%)',
          aspectRatio: '1 / 1',
          top: '49.5%',
          right: 'calc(50% - min(calc(4/3*30cqh), 30%))',
          borderRadius: '50%',
          opacity: 0
        }} />
          </div> : <iframe ref={iframeRef} allow="microphone; autoplay" key={currentURL} src={currentURL} style={{
        width: '100%',
        height: '100%',
        minHeight: 600,
        border: 0
      }} />}
      </Fullscreenable>
      {currentLearningObjectiveHTML && <HighlightedContent title="Learning Objective" image={<img src="/assets/target-icon.png" alt="target" width={60} />} content={<div className="highlighted-description" children={htmlStringToReactNodes(currentLearningObjectiveHTML)} />} styles={{
      backgroundColor: '#206986',
      color: '#fff'
    }} />}
    </Modal>;
};
export default (props => <ActivityPreview {...props} key={(props.previews ?? []).map(p => p.url).join(';')} />);